/**
 * Custom hook to fetch and manage the user's profile image.
 *
 * This hook retrieves the user's profile image from the server using
 * the provided token values and dispatches a Redux action to fetch the image.
 * It also manages loading and error states during the fetch process.
 *
 * @returns {Object} An object containing:
 * - `userProfileImg` (string): The URL of the user's profile image.
 * - `isLoading` (boolean): Indicates whether the profile image is being loaded.
 * - `error` (string | null): Error message if the fetch fails, otherwise `null`.
 */
import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { getUserLogo } from "../../store/User/user-actions";
import GetTokenValues from "../../utils/methods/GetTokenValues";
import { AppDispatch } from "../../store";
import staticContent from "../../utils/staticContent.json";

const useUserProfileImage = () => {
  const dispatch: AppDispatch = useDispatch();
  const [userProfileImg, setUserProfileImg] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const tokenValues = GetTokenValues();

  const fetchUserProfileImage = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!tokenValues) {
        setError(staticContent.tokenNotAvailable);
        return;
      }

      const accessTokenPayload = tokenValues.accessTokenPayload || {
        sub: "",
        ext: { customer: "", tenant_id: "" },
      };

      const customerId = accessTokenPayload.ext.tenant_id;
      const userId = accessTokenPayload.sub;

      const response: any = await dispatch(
        getUserLogo({ customerId, userId })
      ).unwrap();

      if (response.status === 200) {
        setUserProfileImg(response.data || response.payload.data);
      } else {
        setError(staticContent.userProfileImagefetchFailed);
      }
    } catch (err) {
      setError(staticContent.userProfileImagefetchFailed);
      setUserProfileImg("");
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchUserProfileImage();
  }, [fetchUserProfileImage]);

  return { userProfileImg, isLoading, error };
};

export default useUserProfileImage;